/** Onboarding — os passos 2 a 5 do DESIGN.md § 4.3.
 *
 * O passo 1 (cadastro) já aconteceu: quem chega aqui acabou de criar a conta.
 *
 * **O passo 2 apresenta o funil, não deixa escolhê-lo.** A spec previa "escolhe
 * modelo ou usa o padrão", e a API não tem rota de pipeline nem de etapa — não
 * há o que escolher. Mostrar como o funil ficou cumpre o propósito real do
 * passo, que é a pessoa entender onde vai trabalhar; a edição entra quando o
 * backend existir.
 *
 * **O passo 5 (WhatsApp) ficou fora até agora** porque não havia o que
 * conectar: `messaging_wa` não tinha backend, e um passo que promete conectar
 * sem conectar é pior que a ausência dele. O canal existe desde o #127 e ganhou
 * tela no #143 — o guia passa a levar até ela, e não a repetir a configuração.
 * Levar em vez de repetir importa: a configuração da Meta muda, e uma segunda
 * cópia dela aqui envelheceria calada.
 *
 * **O passo só aparece para quem pode conectar.** É `whatsapp:manage`, o mesmo
 * portão do servidor e do botão da barra: o número é da empresa inteira, e
 * oferecê-lo a quem tomaria `403` ensina que o produto quebra.
 *
 * Nada é obrigatório. "Pular" aparece em todos os passos, e o produto é útil
 * ao fim do passo 3 — que é exatamente o que a spec pede.
 */

import { useEffect, useState } from "react";

import { ApiError, FalhaDeRede } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";
import { buscarQuadro, criarOportunidade, type Coluna } from "@/features/board/api";
import { ConvidarPessoa } from "@/features/convites/ConvidarPessoa";

type Passo = "funil" | "oportunidade" | "time" | "whatsapp";

const ORDEM: Record<Passo, number> = { funil: 1, oportunidade: 2, time: 3, whatsapp: 4 };

export function Onboarding({
  nomeDaEmpresa,
  aoConcluir,
  podeConectarWhatsApp = false,
  aoConectarWhatsApp,
}: {
  nomeDaEmpresa: string;
  aoConcluir: () => void;
  /** `whatsapp:manage` — sem isto o guia termina no convite, como antes. */
  podeConectarWhatsApp?: boolean;
  /** Encerra o guia e abre a tela do canal. */
  aoConectarWhatsApp?: () => void;
}) {
  const [passo, setPasso] = useState<Passo>("funil");
  const [colunas, setColunas] = useState<Coluna[]>([]);
  const [titulo, setTitulo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const passoDoWhatsApp = podeConectarWhatsApp && aoConectarWhatsApp !== undefined;
  const totalDePassos = passoDoWhatsApp ? 4 : 3;
  const ultimoPasso = passoDoWhatsApp ? "whatsapp" : "time";

  /** Terminar o convite avança; quem não tem o passo do WhatsApp encerra ali. */
  function depoisDoConvite() {
    if (passoDoWhatsApp) setPasso("whatsapp");
    else aoConcluir();
  }

  // O funil vem do servidor, e não de uma lista fixa aqui: se o provisionamento
  // mudar, o guia mostra o que a pessoa realmente tem — e não o que tinha
  // quando esta tela foi escrita.
  useEffect(() => {
    buscarQuadro()
      .then((quadro) => {
        setColunas(quadro.columns);
      })
      .catch(() => {
        // Falhar aqui não pode travar o onboarding: o funil é ilustração, e a
        // pessoa consegue seguir sem vê-lo.
        setColunas([]);
      });
  }, []);

  async function criar() {
    if (titulo.trim().length < 2 || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await criarOportunidade({ title: titulo.trim() });
      setPasso("time");
    } catch (falha) {
      if (falha instanceof ApiError) setErro(falha.message);
      else if (falha instanceof FalhaDeRede)
        setErro("Não conseguimos falar com o servidor. Verifique sua conexão.");
      else setErro("Algo deu errado. Tente de novo.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-6 py-14">
      <div className="flex w-full max-w-[560px] flex-col gap-6">
        <header className="flex flex-col gap-1">
          <p className="font-texto text-micro uppercase tracking-wide text-texto-tenue">
            Passo {ORDEM[passo]} de {totalDePassos}
          </p>
          <h1 className="font-display text-display-md text-texto">
            {passo === "funil" && `${nomeDaEmpresa} está pronta`}
            {passo === "oportunidade" && "Registre a primeira oportunidade"}
            {passo === "time" && "Chame o time"}
            {passo === "whatsapp" && "Conecte o WhatsApp"}
          </h1>
        </header>

        {erro !== null && (
          <div
            role="alert"
            className="rounded-md border border-carmim/40 bg-carmim/10 px-4 py-3 font-texto text-body text-texto"
          >
            {erro}
          </div>
        )}

        {passo === "funil" && (
          <>
            {/* Duas frases, porque são duas situações. A de cima aponta para
                uma lista — "nestas etapas" —, e a lista pode não existir: ler o
                quadro é secundário aqui e falhar nisso não trava o guia, de
                propósito. Quando falhava, o texto continuava prometendo etapas
                e o passo terminava apontando para o vazio. */}
            <p className="font-texto text-body text-texto-suave">
              {colunas.length > 0
                ? "Seu funil já vem organizado nestas etapas. Os cards andam da esquerda para a direita conforme a negociação avança."
                : "Seu funil já vem organizado em etapas — você as verá em um instante, no quadro. Os cards andam da esquerda para a direita conforme a negociação avança."}
            </p>
            {/* Mesmo vocabulário do cabeçalho de coluna do board: bolinha na
                cor da etapa e nome em micro maiúsculo. A `stage_color` já vinha
                da API e a tela a desperdiçava numa pílula cinza — o guia
                mostrava um funil que não se parecia com o funil. */}
            <ol className="flex flex-wrap gap-2">
              {colunas.map((coluna) => (
                <li
                  key={coluna.stage_id}
                  className="flex items-center gap-2 rounded-sm border border-borda bg-superficie px-3 py-2"
                >
                  <span
                    aria-hidden="true"
                    className="h-[7px] w-[7px] shrink-0 rounded-full"
                    style={{ backgroundColor: coluna.stage_color ?? "rgb(var(--tz-acao))" }}
                  />
                  <span className="font-texto text-micro font-bold uppercase tracking-wide text-texto">
                    {coluna.stage_name}
                  </span>
                </li>
              ))}
            </ol>
            <Botao
              larguraTotal
              tamanho="lg"
              onClick={() => {
                setPasso("oportunidade");
              }}
            >
              Continuar
            </Botao>
          </>
        )}

        {passo === "oportunidade" && (
          <>
            <p className="font-texto text-body text-texto-suave">
              Um título basta. O valor, a empresa e o restante entram depois, quando você souber — o
              produto não exige tudo de uma vez.
            </p>
            <form
              onSubmit={(evento) => {
                evento.preventDefault();
                void criar();
              }}
              aria-label="Primeira oportunidade"
              className="flex flex-col gap-4"
            >
              <Campo
                rotulo="O que você está negociando?"
                value={titulo}
                onChange={(evento) => {
                  setTitulo(evento.target.value);
                }}
                placeholder="Renovação do contrato com a Acme"
                autoFocus
                required
              />
              <Botao
                type="submit"
                larguraTotal
                tamanho="lg"
                carregando={enviando}
                disabled={titulo.trim().length < 2}
              >
                Criar e continuar
              </Botao>
            </form>
          </>
        )}

        {passo === "time" && (
          <>
            {/* A cópia dizia "o convite vira um link — mande pelo WhatsApp".
                Era verdade até o convite passar a sair por e-mail; a partir
                dali, prometia trabalho manual que o produto já fazia sozinho. */}
            <p className="font-texto text-body text-texto-suave">
              Convide quem vende com você. Cada pessoa recebe um e-mail com o acesso — e o link
              aparece aqui também, para mandar pelo WhatsApp se preferir.
            </p>
            <ConvidarPessoa aoFechar={depoisDoConvite} />
          </>
        )}

        {passo === "whatsapp" && (
          <>
            {/* O que o assistente faz, na ordem em que a pessoa vai usar — e
                não a lista de recursos. "Anota", "abre" e "resume" são os três
                verbos que ela reconhece do próprio dia. */}
            <p className="font-texto text-body text-texto-suave">
              Com o canal ligado, sua equipe manda um recado ou um áudio pelo WhatsApp e o Tanzuh
              anota a reunião, abre a oportunidade e devolve o resumo do cliente — sem ninguém abrir
              o sistema.
            </p>
            {/* Os dois tropeços que o roteiro de configuração já conhece
                (`DEPLOY.md § 8.3`), ditos antes de a pessoa esbarrar neles. O
                segundo é o que mais confunde, porque nada falha: a mensagem
                chega, fica gravada, e o assistente não responde. */}
            <ul className="flex flex-col gap-2 rounded-lg border border-borda bg-superficie p-4 font-texto text-caption text-texto-suave">
              <li>
                Você vai precisar do <strong className="text-texto">identificador do número</strong>{" "}
                na Meta — leva uns dois minutos, e não é o telefone.
              </li>
              <li>
                O assistente só obedece a quem ele reconhece: cada vendedor precisa ter o telefone
                cadastrado em <strong className="text-texto">Pessoas</strong>.
              </li>
            </ul>
            <Botao
              larguraTotal
              tamanho="lg"
              onClick={() => {
                aoConectarWhatsApp?.();
              }}
            >
              Conectar o WhatsApp
            </Botao>
          </>
        )}

        {/* Sempre visível, em todos os passos. A spec é explícita: nada além do
            cadastro é obrigatório, e um guia que prende é pior que nenhum. */}
        <button
          type="button"
          onClick={aoConcluir}
          className="rounded-sm self-center font-texto text-caption text-texto-suave underline underline-offset-2 hover:text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao-viva"
        >
          {passo !== ultimoPasso
            ? "Pular e ir para o funil"
            : passo === "whatsapp"
              ? "Concluir sem conectar"
              : "Concluir sem convidar"}
        </button>
      </div>
    </main>
  );
}
